"use client";
import { addDays, format, startOfDay } from "date-fns";
import { flightSearchSchema } from "@/feature/flight/search/schema/search";
import { serializeFlightSearchParams } from "@/feature/flight/search/params/flight-search-params.client";
import { AirportCache } from "@/feature/flight/search/lib/search";
import type { TAirport } from "@/feature/flight/search/types/search";
import {
  DEFAULT_FLIGHT_FORM_VALUES,
  type IDecodedFlightSearch,
  type TFlightSearchFormData,
} from "@/feature/flight/search/types/search";

const FLIGHT_SEARCH_SESSION_KEY = "flight:search:last:v1";

type PersistedFlightSearch = Omit<TFlightSearchFormData, "flights"> & {
  flights: Array<
    Omit<TFlightSearchFormData["flights"][number], "departDate"> & {
      departDate?: string | null;
    }
  >;
};

// Helper to hydrate airports from cache
function hydrateFlights(flights: TFlightSearchFormData["flights"]): TFlightSearchFormData["flights"] {
  return flights.map(f => {
    let from = f.from;
    let to = f.to;
    if (from.iata_code) {
      if (from.name) {
        AirportCache.set(from);
      } else {
        const cached = AirportCache.get(from.iata_code);
        if (cached.name) {
          from = cached;
        }
      }
    }
    if (to.iata_code) {
      if (to.name) {
        AirportCache.set(to);
      } else {
        const cached = AirportCache.get(to.iata_code);
        if (cached.name) {
          to = cached;
        }
      }
    }
    return {
      ...f,
      from,
      to,
    };
  });
}

function withDynamicDates(
  tripType: TFlightSearchFormData["tripType"],
  flights: TFlightSearchFormData["flights"]
): TFlightSearchFormData["flights"] {
  const today = startOfDay(new Date());
  let previousDate: Date | undefined;

  return flights.map((flight, index) => {
    const explicitDate = flight.departDate ?? undefined;
    if (explicitDate) {
      previousDate = explicitDate;
      return flight;
    }

    let fallbackDate: Date | undefined;

    if (index === 0) {
      fallbackDate = today;
    } else if (tripType === "roundtrip" && index === 1) {
      fallbackDate = addDays(previousDate ?? today, 4);
    } else if (previousDate) {
      fallbackDate = addDays(previousDate, 1);
    }

    if (!fallbackDate) return flight;

    previousDate = fallbackDate;
    return {
      ...flight,
      departDate: fallbackDate,
    };
  });
}

function cloneFlights(
  flights: TFlightSearchFormData["flights"],
): TFlightSearchFormData["flights"] {
  return flights.map((flight) => ({
    ...flight,
    from: { ...flight.from },
    to: { ...flight.to },
  }));
}

function normalizeFormData(data: TFlightSearchFormData): TFlightSearchFormData {
  const hydratedFlights = hydrateFlights(cloneFlights(data.flights));

  return {
    tripType: data.tripType,
    fareType: data.fareType,
    flights: withDynamicDates(data.tripType, hydratedFlights),
    passengers: { ...data.passengers },
    cabin: data.cabin,
    childrenAges: [...data.childrenAges],
    preferredAirlines: data.preferredAirlines
      ? [...data.preferredAirlines]
      : undefined,
  };
}

function parseStoredDate(value: string | null | undefined) {
  if (!value) return null;

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function createInitialData(
  serverInitialData?: IDecodedFlightSearch | null
): TFlightSearchFormData {
  if (serverInitialData) {
    return normalizeFormData(serverInitialData);
  }

  return normalizeFormData(DEFAULT_FLIGHT_FORM_VALUES);
}

export function generateSearchParams(data: TFlightSearchFormData): string {
  const route = data.flights
    .filter(f => f.from.iata_code && f.to.iata_code)
    .map(f => ({
      from: f.from,
      to: f.to,
      departDate: f.departDate ? format(f.departDate, "yyyy-MM-dd") : undefined
    }));
  const params = {
    trip: data.tripType,
    route,
    fare: data.fareType,
    pax: data.passengers,
    cabin: data.cabin,
    ages: data.childrenAges.filter((a): a is number => a != null)
  };
  return serializeFlightSearchParams(params);
}

export function registerAirport(airport: TAirport): void {
  AirportCache.set(airport);
}

export function saveLastFlightSearch(data: TFlightSearchFormData): void {
  if (typeof window === "undefined") return;

  const payload: PersistedFlightSearch = {
    tripType: data.tripType,
    fareType: data.fareType,
    passengers: { ...data.passengers },
    cabin: data.cabin,
    childrenAges: [...data.childrenAges],
    preferredAirlines: data.preferredAirlines
      ? [...data.preferredAirlines]
      : undefined,
    flights: data.flights.map((flight) => ({
      ...flight,
      from: { ...flight.from },
      to: { ...flight.to },
      departDate: flight.departDate ? flight.departDate.toISOString() : null,
    })),
  };

  try {
    window.sessionStorage.setItem(
      FLIGHT_SEARCH_SESSION_KEY,
      JSON.stringify(payload),
    );
  } catch {
    // Ignore sessionStorage failures.
  }
}

export function getLastFlightSearch(): TFlightSearchFormData | null {
  if (typeof window === "undefined") return null;

  try {
    const raw = window.sessionStorage.getItem(FLIGHT_SEARCH_SESSION_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as PersistedFlightSearch;
    const candidate: TFlightSearchFormData = {
      tripType: parsed.tripType,
      fareType: parsed.fareType,
      passengers: parsed.passengers,
      cabin: parsed.cabin,
      childrenAges: parsed.childrenAges ?? [],
      preferredAirlines: parsed.preferredAirlines,
      flights: (parsed.flights ?? []).map((flight) => ({
        ...flight,
        from: { ...flight.from },
        to: { ...flight.to },
        departDate: parseStoredDate(flight.departDate),
      })),
    };

    const result = flightSearchSchema.safeParse(candidate);
    if (!result.success) {
      window.sessionStorage.removeItem(FLIGHT_SEARCH_SESSION_KEY);
      return null;
    }

    return normalizeFormData(result.data);
  } catch {
    window.sessionStorage.removeItem(FLIGHT_SEARCH_SESSION_KEY);
    return null;
  }
}




